import Vue from "vue";
import App from "./App.vue";
import router from "./router";
import store from "./store";
import axios from "axios";
import Swal from "sweetalert2/dist/sweetalert2.js";
import "sweetalert2/dist/sweetalert2.min.css";
import { clearCatalogCache } from "./utils/filterCache";
import { initGlobalDraggableModals } from "./utils/draggableModal";

// Inicializar modales arrastrables / movibles en toda la aplicación
initGlobalDraggableModals();


let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// Interceptor de Request: agrega token de autenticación y maneja loading
axios.interceptors.request.use(
  config => {
    const token = localStorage.getItem("authToken");
    if (!config.headers) {
      config.headers = {};
    }
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
      if (typeof config.headers.set === "function") {
        config.headers.set("Authorization", `Bearer ${token}`);
      }
    }
    config.headers["Content-Type"] = "application/json";
    config.headers["Accept"] = "application/json";

    const skip = config.skipLoading || (config.headers && config.headers["X-Skip-Loading"]);
    if (!skip) {
      store.commit("setLoading", true);
    }
    return config;
  },
  error => {
    const skip = error.config && (error.config.skipLoading || (error.config.headers && error.config.headers["X-Skip-Loading"]));
    if (!skip) {
      store.commit("setLoading", false);
    }
    return Promise.reject(error);
  }
);

// Interceptor de Response: decrementa loading y maneja errores globales con renovación de token
axios.interceptors.response.use(
  response => {
    const skip = response.config && (response.config.skipLoading || (response.config.headers && response.config.headers["X-Skip-Loading"]));
    if (!skip) {
      store.commit("setLoading", false);
    }

    // Invalidación automática de caché de catálogos al realizar cualquier mutación (crear, editar, borrar, toggle)
    try {
      const url = ((response && response.config && response.config.url) || "").toLowerCase();
      const mutationKeywords = ["update", "add", "new", "del", "delete", "toggle", "aprobacion", "atributos", "save"];
      const isMutation = mutationKeywords.some(kw => url.includes(`/${kw}`));

      if (isMutation) {
        const knownEntities = [
          "producto", "operador", "keyword",
          "tiposuscripcion", "billingsequence", "servicio",
          "atributo", "integrador", "club"
        ];
        let matchedEntity = null;
        for (const ent of knownEntities) {
          if (url.includes(`/${ent}/`) || url.includes(`/${ent}`)) {
            matchedEntity = ent;
            break;
          }
        }
        if (matchedEntity) {
          clearCatalogCache(matchedEntity);
        } else {
          clearCatalogCache();
        }
      }
    } catch (err) {
      console.warn("Error invalidating catalog cache:", err);
    }

    return response;
  },
  error => {
    const skip = error.config && (error.config.skipLoading || (error.config.headers && error.config.headers["X-Skip-Loading"]));
    if (!skip) {
      store.commit("setLoading", false);
    }
    const originalRequest = error.config;
    const url = (originalRequest && originalRequest.url) || "";
    const isLoginEndpoint = url.includes("auth/login") || url.includes("user/login") || url.endsWith("/login");
    const isRefreshEndpoint = url.includes("auth/refresh") || url.includes("user/refresh") || url.endsWith("/refresh");
    const isAuthEndpoint = isLoginEndpoint || isRefreshEndpoint;

    if (!error.response) {
      if (isLoginEndpoint) {
        store.commit("setError", "No se puede conectar al servidor");
      } else {
        Swal.fire({
          title: "Error de Conexión",
          text: "No se puede conectar al servidor",
          icon: "error",
          confirmButtonText: "Aceptar"
        });
      }
      return Promise.reject(error);
    }

    const status = error.response.status;
    const data = error.response.data || {};
    
    // Extraer mensaje de error de manera flexible (FastAPI o Laravel format)
    let errorMessage = "Ocurrió un error inesperado";
    if (typeof data === "string") {
      errorMessage = data;
    } else if (data.detail) {
      if (typeof data.detail === "string") {
        errorMessage = data.detail;
      } else if (Array.isArray(data.detail) && data.detail.length && data.detail[0].msg) {
        errorMessage = data.detail[0].msg;
      } else {
        errorMessage = null;
      }
    } else if (data.message) {
      errorMessage = data.message;
    } else if (data.error) {
      if (typeof data.error === "string") {
        errorMessage = data.error;
      } else if (data.error.message) {
        errorMessage = data.error.message;
      } else {
        errorMessage = null;
      }
    }

    // Manejo específico para el endpoint de login (cualquier status de error: 400, 401, 403, 422, etc.)
    if (isLoginEndpoint) {
      let errors = data.errors || (data.error && data.error.data && data.error.data.errors) || null;
      if (!errors && data.detail && Array.isArray(data.detail)) {
        errors = {};
        data.detail.forEach(item => {
          const field = item.loc && item.loc[item.loc.length - 1];
          if (field) {
            errors[field] = [item.msg];
          }
        });
      }
      store.commit("setErrors", errors || {});
      store.commit("setError", errorMessage || "Error de usuario o contraseña");
      return Promise.reject(error);
    }

    if (status === 500) {
      Swal.fire({
        title: "Error de Servidor (500)",
        text: errorMessage || "Error interno del servidor",
        icon: "error",
        confirmButtonText: "Aceptar"
      });
      return Promise.reject(error);
    } else if (status === 403) {
      Swal.fire({
        title: "Acceso Denegado (403)",
        text: errorMessage || "Acceso denegado: no posee los permisos suficientes",
        icon: "warning",
        confirmButtonText: "Aceptar"
      });
      return Promise.reject(error);
    } else if (status === 422) {
      let errors = data.errors || (data.error && data.error.data && data.error.data.errors) || null;
      if (!errors && data.detail && Array.isArray(data.detail)) {
        errors = {};
        data.detail.forEach(item => {
          const field = item.loc && item.loc[item.loc.length - 1];
          if (field) {
            errors[field] = [item.msg];
          }
        });
      }
      store.commit("setErrors", errors || {});
      store.commit("setError", errorMessage || null);
      return Promise.reject(error);
    } else if (status === 401) {
      if (isAuthEndpoint) {
        return Promise.reject(error);
      }

      const refreshToken = localStorage.getItem("refreshToken");

      if (refreshToken && !originalRequest._retry) {
        if (isRefreshing) {
          return new Promise((resolve, reject) => {
            failedQueue.push({ resolve, reject });
          })
            .then(token => {
              originalRequest.headers.common["Authorization"] = "Bearer " + token;
              originalRequest.headers["Authorization"] = "Bearer " + token;
              return axios(originalRequest);
            })
            .catch(err => {
              return Promise.reject(err);
            });
        }

        originalRequest._retry = true;
        isRefreshing = true;

        return new Promise((resolve, reject) => {
          axios
            .post(
              process.env.VUE_APP_API_URL + "auth/refresh",
              { refresh_token: refreshToken },
              { headers: { Authorization: "" } }
            )
            .then(({ data: refreshData }) => {
              if (refreshData && refreshData.data && refreshData.data.token) {
                const newToken = refreshData.data.token;
                localStorage.setItem("authToken", newToken);
                if (refreshData.data.refresh_token) {
                  localStorage.setItem("refreshToken", refreshData.data.refresh_token);
                }
                if (refreshData.data.user) {
                  store.commit("auth/setUserData", refreshData.data.user);
                }
                originalRequest.headers.common["Authorization"] = "Bearer " + newToken;
                originalRequest.headers["Authorization"] = "Bearer " + newToken;
                processQueue(null, newToken);
                resolve(axios(originalRequest));
              } else {
                throw new Error("Respuesta inválida al renovar token");
              }
            })
            .catch(refreshErr => {
              processQueue(refreshErr, null);
              localStorage.removeItem("authToken");
              localStorage.removeItem("refreshToken");
              localStorage.removeItem("integrador");
              store.commit("auth/setUserData", null);
              store.commit("resetLoading");
              try {
                Swal.close();
              } catch (e) {
                /* ignore */
              }

              if (router.currentRoute.name !== "Login" && window.location.pathname !== "/login") {
                window.location.href = "/login";
              }
              reject(refreshErr);
            })
            .finally(() => {
              isRefreshing = false;
            });
        });
      }

      // Sin refresh_token o falló el reintento
      localStorage.removeItem("authToken");
      localStorage.removeItem("refreshToken");
      localStorage.removeItem("integrador");
      store.commit("auth/setUserData", null);
      store.commit("resetLoading");
      try {
        Swal.close();
      } catch (e) {
        /* ignore */
      }

      if (router.currentRoute.name !== "Login" && window.location.pathname !== "/login") {
        window.location.href = "/login";
      }
      return Promise.reject(error);
    } else {
      return Promise.reject(error);
    }
  }
);



new Vue({
  router,
  store,
  render: h => h(App)
}).$mount("#app");
